import { FC, useState, KeyboardEvent, useMemo, ChangeEvent } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useStyles } from './styles';
import { Paper, Grid, TextField, Select, MenuItem } from '@material-ui/core';
import Button from '@material-ui/core/Button';

export const LdapServerConnection: FC = observer(() => {
  const {
    importStore: {
      ldapConnection,
      ldapConnect,
      setStep,
      updateLdapConnectionHost,
      updateLdapConnectionPort,
      updateLdapConnectionSecurityType,
      updateLdapConnectionUsername,
      updateLdapConnectionPassword,
      updateLdapConnectionBaseDn,
    },
    notificationStore: { openNotification },
    appPreloaderStore: { disablePreloader },
  } = useStore();

  const [isValidUserDN, setIsValidUserDN] = useState(true);
  const [isValidBaseDN, setIsValidBaseDN] = useState(true);

  const { mutate, isLoading } = useMutation('ldapConnect', () => ldapConnect(), {
    onMutate: () => {
      setStep('one_status');
    },
    onSuccess: () => {
      setStep('three');
      disablePreloader();
    },
    onError: ({ message }: Error) => {
      setStep('two');
      openNotification('error', message);
    },
  });

  useAppPreloader(isLoading);

  // const hostname = window.location.hostname.split('.');

  const classes = useStyles();

  const isSubmitAllowed = useMemo(
    () =>
      ldapConnection.host && ldapConnection.port && ldapConnection.username && ldapConnection.password && !isLoading
        ? false
        : true,
    [ldapConnection.host, ldapConnection.port, ldapConnection.username, ldapConnection.password, isLoading]
  );

  const handleEnterPressed = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Enter' && !isSubmitAllowed) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (/=/.test(ldapConnection.username)) {
      setIsValidUserDN(true);
      mutate();
    } else {
      setIsValidUserDN(false);
    }
  };

  return (
    <>
      <div className={classes.textBox}>
        {/* Please input your existing LDAP information for synchronizing with the Cloud directory. Once synchronization is
        successful, your user data is available at{' '}
        <strong>
          {hostname[0]}-ldap.{hostname[1]}.{hostname[2]}
        </strong> */}
        Please input your existing LDAP server information so that it can be synchronized with the EasyIdentity Cloud
        Directory. Once the import is successful, you will be able to able to manage the user data in this EasyIdentity
        Dashboard.
      </div>
      <Grid container spacing={2} onKeyDownCapture={handleEnterPressed}>
        <Grid item sm={10}>
          <Paper className={classes.tabPaper}>
            <Grid container className={classes.row}>
              <p className={classes.title}>Hostname:</p>
              <TextField
                className={classes.desc}
                key='host'
                placeholder='ldap.example.com'
                value={ldapConnection.host}
                onChange={(e) => updateLdapConnectionHost(e.target.value.toString())}
              />
            </Grid>
            <Grid container className={classes.row}>
              <p className={classes.title}>Port:</p>
              <TextField
                className={classes.desc}
                key='port'
                placeholder='389 or 636'
                value={ldapConnection.port}
                onChange={(e) => updateLdapConnectionPort(e.target.value.toString())}
              />
            </Grid>
            <Grid container className={classes.row}>
              <p className={classes.title}>Security Type:</p>
              <Select
                defaultValue={'none'}
                value={ldapConnection.securityType}
                className={classes.desc}
                onChange={(event: ChangeEvent<{ value: unknown }>) =>
                  updateLdapConnectionSecurityType(event.target.value as string)
                }>
                <MenuItem key='none' value='none'>
                  NONE
                </MenuItem>
                <MenuItem key='tls' value='tls'>
                  TLS
                </MenuItem>
                <MenuItem key='ssl' value='ssl'>
                  SSL
                </MenuItem>
              </Select>
            </Grid>
            <Grid container className={classes.row}>
              <p className={classes.title}>BaseDN (optional):</p>
              <TextField
                className={classes.desc}
                key='baseDn'
                error={!isValidBaseDN}
                helperText={isValidBaseDN ? '' : `Please use correct format: dc=ldap,dc=example,dc=com`}
                placeholder='dc=ldap,dc=example,dc=com'
                value={ldapConnection.baseDn}
                onChange={(e) => {
                  setIsValidBaseDN(
                    /(\w{1,2}={1}[\w -]+$)|(\w{1,2}={1}[\w -]+[,])+(\w{1,2}={1}[\w -]+$)/.test(
                      e.target.value.toString()
                    )
                  );
                  updateLdapConnectionBaseDn(e.target.value.toString());
                }}
              />
            </Grid>
            <Grid container className={classes.row}>
              <p className={classes.title}>UserDN:</p>
              <TextField
                className={classes.desc}
                error={!isValidUserDN}
                helperText={
                  isValidUserDN
                    ? ''
                    : `Please use correct format: uid=admin OR cn=admin OR uid=admin,cn=users,cn=accounts,dc=test,dc=local`
                }
                key='username'
                placeholder='cn=admin,dc=ldap,dc=example,dc=com'
                value={ldapConnection.username}
                onChange={(e) => {
                  setIsValidUserDN(
                    /(\w{1,2}={1}[\w -]+$)|(\w{1,2}={1}[\w -]+[,])+(\w{1,2}={1}[\w -]+$)/.test(
                      e.target.value.toString()
                    )
                  );
                  updateLdapConnectionUsername(e.target.value.toString());
                }}
              />
            </Grid>
            <Grid container className={classes.row}>
              <p className={classes.title}>Password:</p>
              <TextField
                type='password'
                className={classes.desc}
                key='password'
                placeholder='password'
                value={ldapConnection.password}
                onChange={(e) => updateLdapConnectionPassword(e.target.value.toString())}
              />
            </Grid>
          </Paper>
        </Grid>
        {/* {ldapAllConnections.length ? (
          <Grid item md={6}>
            <Paper className={classes.tabPaper}>
              <h3 className={classes.listSourseLabel}>My User Data Sources</h3>
              <List component='nav'>
                {ldapAllConnections.map((i) => (
                  <ListItem
                    key={`list_${i}`}
                    button
                    className={i.active === '1' ? classes.active : ''}
                    onClick={() => {
                      selectLdapConnection({ ...i });
                    }}>
                    <ListItemText primary={i.host} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        ) : null} */}
      </Grid>
      <div className={classes.btnBox}>
        <Button className={classes.btn} onClick={() => setStep('one')}>
          Back
        </Button>
        <Button
          variant='contained'
          color='secondary'
          className={classes.btn}
          disabled={isSubmitAllowed}
          onClick={handleSubmit}>
          Next
        </Button>
      </div>
    </>
  );
});
